import type { AccountUse, FinancialAccount } from '../types/family-finance'

/**
 * Personal vs. business classification for accounts.
 *
 * The family-finance service doesn't carry a `use` field — SimpleFIN reports a
 * name, a mask, and a balance, and nothing about whether the account belongs to
 * the household or to one of the LLCs. So the owner's choice is kept here, in
 * localStorage, keyed by account id, and layered over whatever `listAccounts`
 * returns (mock store or live API alike).
 *
 * Precedence: the owner's stored choice wins; then anything the API supplies
 * (so this keeps working unchanged if the service grows the field); then a
 * name-based guess, which is only ever a starting point the owner can correct.
 */

const STORE_KEY = 'nb-family-account-use-v1'

export interface AccountClassification {
  use: AccountUse
  entityId: string | null
}

type Store = Record<string, AccountClassification>

let _memory: Store | null = null

function load(): Store {
  if (_memory) return _memory
  try {
    const raw = localStorage.getItem(STORE_KEY)
    _memory = raw ? (JSON.parse(raw) as Store) : {}
  } catch {
    _memory = {}
  }
  return _memory
}

function save(store: Store): void {
  _memory = store
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store))
  } catch {
    // storage unavailable — keep the choice for this session only
  }
}

// Entity markers a bank actually puts in an account or institution name. Kept
// deliberately narrow — a wrong "business" guess is worse than a wrong
// "personal" one, since it quietly moves a balance out of the household view.
// Notably absent: Trust, Holdings, Co, and Commercial, which show up in the
// names of banks that hold plenty of ordinary personal accounts.
const ENTITY_MARKER = /\b(LLC|L\.L\.C\.|PLLC|P\.L\.L\.C\.|INC|CORP|LTD|LLP|BUSINESS)\b/i

/** Best guess for an account the owner hasn't classified yet. */
export function inferUse(account: FinancialAccount): AccountUse {
  const haystack = `${account.name} ${account.institutionName} ${account.owner}`
  return ENTITY_MARKER.test(haystack) ? 'business' : 'personal'
}

export function getClassification(id: string): AccountClassification | null {
  return load()[id] ?? null
}

export function setClassification(id: string, value: AccountClassification): void {
  const store = { ...load() }
  store[id] = { use: value.use, entityId: value.use === 'business' ? value.entityId : null }
  save(store)
}

export function clearClassification(id: string): void {
  const store = { ...load() }
  delete store[id]
  save(store)
}

/**
 * Resolve `use` / `entityId` for a full account list. Callers pass everything
 * they got back, so this is also where entries for deleted accounts are dropped.
 */
export function applyClassification(accounts: FinancialAccount[]): FinancialAccount[] {
  const store = load()
  const live = new Set(accounts.map((a) => a.id))
  const stale = Object.keys(store).filter((id) => !live.has(id))
  if (stale.length) {
    const pruned = { ...store }
    stale.forEach((id) => delete pruned[id])
    save(pruned)
  }

  return accounts.map((account) => {
    const chosen = store[account.id]
    if (chosen) return { ...account, use: chosen.use, entityId: chosen.entityId ?? null }
    const use = account.use ?? inferUse(account)
    return { ...account, use, entityId: use === 'business' ? account.entityId ?? null : null }
  })
}
